import type { z } from 'zod';
import { getClassLabel } from '../classes/codecs';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, decodeRecordId, encodeRecordId } from '../recordIds';
import { DBLessonSchema, LessonSchema } from './schemas';

type DBLesson = z.infer<typeof DBLessonSchema>;
type Lesson = z.infer<typeof LessonSchema>;

/** The subject and class code a lesson's stored partition key, `subject#classCode`, holds. */
const parseSubjectClassIdentifier = (subjectClassIdentifier: string) => {
  const [subject, classCode] = subjectClassIdentifier.split('#');
  return { subject: subject as Subject, classCode: classCode as string };
};

/** The course, template code and counter a lesson's stored sort key holds. */
const parseLessonKey = (lesson: string) => {
  const [courseId, lessonTemplateCode, counter] = lesson.split('#');
  return {
    courseId: courseId as string,
    lessonTemplateCode: lessonTemplateCode as string,
    counter: counter as string,
  };
};

export const LessonCodecConfig: TransformerConfig<
  typeof LessonSchema,
  typeof DBLessonSchema,
  'id' | 'classId' | 'lessonTemplateId' | 'classLabel'
> = {
  name: 'Lesson',
  clientSchema: LessonSchema,
  dbSchema: DBLessonSchema,
  transformers: [
    // subject, classCode ↔ subjectClassIdentifier: 'subject#classCode'
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['subject', 'classCode'] as const,
        dependencies: ['subject', 'classCode'] as const,
        encode: ({ subject, classCode }) => ({
          subjectClassIdentifier: `${subject}#${classCode}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectClassIdentifier'] as const,
        decode: ({ subjectClassIdentifier }) =>
          subjectClassIdentifier === undefined
            ? {}
            : parseSubjectClassIdentifier(subjectClassIdentifier),
      },
    }),

    // courseId, lessonTemplateCode, counter ↔ lesson: 'courseId#lessonTemplateCode#counter',
    // and courseLesson: 'courseId#lessonTemplateCode'
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['courseId', 'lessonTemplateCode', 'counter'] as const,
        dependencies: ['courseId', 'lessonTemplateCode', 'counter'] as const,
        encode: ({ courseId, lessonTemplateCode, counter }) => ({
          lesson: `${courseId}#${lessonTemplateCode}#${counter}`,
          courseLesson: `${courseId}#${lessonTemplateCode}`,
        }),
      },
      decoder: {
        sourceFields: ['lesson', 'courseLesson'] as const,
        decode: ({ lesson }) => (lesson === undefined ? {} : parseLessonKey(lesson)),
      },
    }),

    // enrolledStudents: [{ studentId, name, lessonEnrolmentId }] ↔
    //   enrolledStudents: { studentId: { name } }
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['enrolledStudents'] as const,
        encode: ({ enrolledStudents }) => ({
          enrolledStudents: Object.fromEntries(
            (enrolledStudents as Lesson['enrolledStudents']).map(val => [
              val.studentId,
              { name: val.name },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['enrolledStudents', 'subjectClassIdentifier', 'lesson'] as const,
        // Each enrolment's id needs the lesson's key, so a read without it gives no roster
        decode: ({ enrolledStudents, subjectClassIdentifier, lesson }) => {
          if (
            enrolledStudents === undefined ||
            subjectClassIdentifier === undefined ||
            lesson === undefined
          ) {
            return {};
          }
          const lessonParts = {
            ...parseSubjectClassIdentifier(subjectClassIdentifier),
            ...parseLessonKey(lesson),
          };
          return {
            enrolledStudents: Object.entries(
              enrolledStudents as NonNullable<DBLesson['enrolledStudents']>
            ).map(([studentId, { name }]) => ({
              studentId,
              name,
              lessonEnrolmentId: encodeRecordId('lessonEnrolment', { studentId, ...lessonParts }),
            })),
          };
        },
      },
    }),

    // rollMarked: boolean ↔ rollMarked: 'true' | 'false'
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['rollMarked'] as const,
        encode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as Lesson['rollMarked']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['rollMarked'] as const,
        decode: ({ rollMarked }) => ({
          rollMarked: (rollMarked as DBLesson['rollMarked']) === 'true',
        }),
      },
    }),

    // roomId ↔ room: the room's code, its location being the lesson's own
    createTransformer<typeof LessonSchema, typeof DBLessonSchema>({
      encoder: {
        sourceFields: ['roomId'] as const,
        encode: ({ roomId }) => ({ room: decodeRecordId('room', roomId as Lesson['roomId']).code }),
      },
      decoder: {
        sourceFields: ['location', 'room'] as const,
        decode: ({ location, room }) => ({
          ...(location !== undefined && { location }),
          ...(location !== undefined &&
            room !== undefined && { roomId: encodeRecordId('room', { location, code: room }) }),
        }),
      },
    }),

    createRenameTransformer<typeof LessonSchema, typeof DBLessonSchema>('questionSetId', 'quiz'),
    createRenameTransformer<typeof LessonSchema, typeof DBLessonSchema>('streamId', 'stream'),
    createRenameTransformer<typeof LessonSchema, typeof DBLessonSchema>('teacherId', 'teacher', {
      absentAs: '',
      alwaysStored: true,
    }),
  ],
  derived: {
    id: lesson => buildRecordId('lesson', lesson),
    classId: lesson => buildRecordId('class', { subject: lesson.subject, code: lesson.classCode }),
    lessonTemplateId: lesson =>
      buildRecordId('lessonTemplate', {
        courseId: lesson.courseId,
        code: lesson.lessonTemplateCode,
      }),
    classLabel: lesson =>
      lesson.courseId !== undefined && lesson.classCode !== undefined
        ? getClassLabel({ courseId: lesson.courseId, code: lesson.classCode })
        : undefined,
  },
};

export const encodeLessonToDb = createEncodeToDbFn(LessonCodecConfig);
export const decodeLessonFromDb = createDecodeFromDbFn(LessonCodecConfig);
